import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setStoredToken } from "./api";

const AuthContext = createContext(null);

/** Apply a school's brand profile, or reset to corporate defaults.
 *  Sets CSS variables on :root so any component using
 *  text-[var(--brand-primary)] / bg-[var(--brand-primary)] / etc.
 *  picks up the per-school theme instantly.
 */
export function applyTheme(palette, brandProfile) {
  const root = document.documentElement;
  const styleId = "brand-theme-overrides";
  const fontLinkId = "brand-font-links";
  const heroId = "brand-hero-backdrop";
  let styleEl = document.getElementById(styleId);
  let fontLinksEl = document.getElementById(fontLinkId);
  let heroEl = document.getElementById(heroId);

  const reset = () => {
    [
      "--brand-primary",
      "--brand-primary-fg",
      "--brand-accent",
      "--brand-dark",
      "--brand-light",
      "--brand-font-heading",
      "--brand-font-body",
    ].forEach((v) => root.style.removeProperty(v));
    root.dataset.brand = "platform";
    root.dataset.vibe = "corporate";
    if (styleEl) styleEl.textContent = "";
    if (fontLinksEl) fontLinksEl.innerHTML = "";
    if (heroEl) heroEl.style.display = "none";
  };

  if (!palette || (!palette.primary && !palette.accent)) {
    reset();
    return;
  }

  // ---------- Resolve tokens ----------
  const primary = palette.primary || "#002D62";
  const accent  = palette.accent  || "#FFA300";
  const dark    = palette.dark    || "#0A1128";
  const light   = palette.light   || "#F0F4F8";
  const bp = brandProfile || {};
  const vibe = bp.vibe || "corporate";
  const headingFont = bp.heading_font;
  const bodyFont    = bp.body_font || headingFont;

  // Foreground color for buttons painted in --brand-primary
  // (auto-pick black/white based on perceived luminance of primary)
  const hex2rgb = (h) => {
    const c = h.replace("#", "");
    const n = parseInt(c.length === 3 ? c.split("").map((x) => x + x).join("") : c, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const [pr, pg, pb] = hex2rgb(primary);
  const luma = (0.2126 * pr + 0.7152 * pg + 0.0722 * pb) / 255;
  const primaryFg = luma > 0.6 ? "#0F172A" : "#FFFFFF";

  root.style.setProperty("--brand-primary",    primary);
  root.style.setProperty("--brand-primary-fg", primaryFg);
  root.style.setProperty("--brand-accent",     accent);
  root.style.setProperty("--brand-dark",       dark);
  root.style.setProperty("--brand-light",      light);
  if (headingFont) root.style.setProperty("--brand-font-heading", `"${headingFont}", Chivo, sans-serif`);
  if (bodyFont)    root.style.setProperty("--brand-font-body",    `"${bodyFont}", "IBM Plex Sans", sans-serif`);
  root.dataset.brand = "school";
  root.dataset.vibe  = vibe;

  // ---- Inject the school's Google Fonts ----
  if (!fontLinksEl) {
    fontLinksEl = document.createElement("div");
    fontLinksEl.id = fontLinkId;
    fontLinksEl.style.display = "none";
    document.head.appendChild(fontLinksEl);
  }
  fontLinksEl.innerHTML = "";
  const seen = new Set();
  (bp.font_links || []).forEach((href) => {
    if (seen.has(href)) return;
    seen.add(href);
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = href;
    fontLinksEl.appendChild(l);
  });
  if (headingFont && !(bp.font_links || []).length) {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(headingFont).replace(/%20/g, "+")}:wght@400;600;700&display=swap`;
    fontLinksEl.appendChild(l);
  }

  // ---- Hero backdrop (drop the school's hero image very softly behind chrome) ----
  if (!heroEl) {
    heroEl = document.createElement("div");
    heroEl.id = heroId;
    heroEl.style.cssText =
      "position:fixed;inset:0;z-index:-1;pointer-events:none;background-size:cover;background-position:center;opacity:0.06;filter:saturate(0.85);";
    document.body.appendChild(heroEl);
  }
  if (bp.hero_image_url) {
    heroEl.style.backgroundImage = `url("${bp.hero_image_url}")`;
    heroEl.style.display = "block";
  } else {
    heroEl.style.display = "none";
  }

  // ---- Per-school stylesheet ----
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    /* Heading / body font overrides — only when school provides them */
    ${headingFont ? `h1,h2,h3,h4,h5,h6,.font-display,.font-stencil,.font-heading { font-family: var(--brand-font-heading) !important; }` : ""}
    ${bodyFont ? `body,.font-body { font-family: var(--brand-font-body) !important; }` : ""}
  `;
}


export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyTheme(data?.school?.theme_palette, data?.school?.brand_profile);
    } catch (e) {
      setUser(null);
      applyTheme(null, null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setStoredToken("");
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }

export function loginWithGoogle() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
